import { useEffect, useState } from 'react';

import {
  sortConversionUnits,
  toConversionUnitInputs,
} from '@atom/components/common/conversionUnitsTable/conversionUnitUtilities';
import CreateEditSchemaModalContext, {
  ModalStep,
} from '@atom/components/common/createEditSchemaModal/CreateEditSchemaModalContext';
import { Button, Icon, Modal } from '@atom/mui';
import { ChevronLeftIcon, ChevronRightIcon } from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import {
  ConversionUnit,
  LocationType,
  SchemaBudgetType,
  SchemaSavePayload,
  SchemaTree,
  StartEndReadingOption,
  StockBasedOption,
} from '@atom/types/schema';

import BasicDetailsTab from './BasicDetailsTab';
import CostAndUnitTab from './CostAndUnitTab';
import CostTrackingTab from './CostTrackingTab';
import {
  getModalStepsArray,
  getStepIndex,
  isCurrentStepDisabled,
} from './createEditSchemaUtilities';
import StepperSidebar from './StepperSidebar';

import classes from './createEditSchemaModal.module.css';

const styles = {
  modalContent: { padding: 0 },
  cancelButton: { marginRight: '0.5rem' },
};

type CreateEditSchemaModalProps = {
  open: boolean;
  onClose: () => void;
  /**
   * on edit, conversionUnits is the list the schema should end up with, or
   * undefined when they shouldn't change. schemaUpdate doesn't accept
   * conversion units, so the caller saves them with their own mutations.
   */
  onSave: (
    payload: SchemaSavePayload,
    conversionUnits?: ConversionUnit[],
  ) => void;
  schema?: Partial<SchemaTree>;
};

const CreateEditSchemaModal = ({
  open,
  onClose,
  onSave,
  schema,
}: CreateEditSchemaModalProps) => {
  const [activeStep, setActiveStep] = useState<ModalStep>(
    ModalStep.BASIC_DETAILS,
  );
  const [name, setName] = useState<string>(schema?.name ?? '');
  const [locationType, setLocationType] = useState<LocationType | null>(
    schema?.locationType ?? null,
  );
  const [colorId, setColorId] = useState<number>(schema?.colorId ?? 0);
  const [markerId, setMarkerId] = useState<number>(schema?.markerId ?? 0);
  const [isMaterial, setIsMaterial] = useState<boolean>(
    schema?.isMaterial ?? false,
  );
  const [stockBasedOption, setStockBasedOption] = useState<StockBasedOption>(
    schema?.budget?.stockBasedOption ?? StockBasedOption.NONE,
  );
  const [startEndReadingOption, setStartEndReadingOption] =
    useState<StartEndReadingOption>(
      schema?.budget?.startEndReadingOption ?? StartEndReadingOption.NONE,
    );
  const [costType, setCostType] = useState<SchemaBudgetType>(
    schema?.budget?.type ?? SchemaBudgetType.VARIABLE,
  );
  const [rate, setRate] = useState<number | null>(schema?.budget?.rate ?? null);
  const [unit, setUnit] = useState<string>(schema?.budget?.unit ?? '');
  const [conversionUnits, setConversionUnits] = useState<ConversionUnit[]>(
    sortConversionUnits(schema?.budget?.conversionUnits ?? []),
  );

  const STEPS_ARRAY = getModalStepsArray(isMaterial);
  const isEdit = !!schema;

  const supportsConversionUnits = [
    SchemaBudgetType.VARIABLE_COST,
    SchemaBudgetType.FIXED,
  ].includes(costType);

  // schemaUpdate doesn't accept conversion units, so they're only sent here on
  // create. Edits are handed to onSave separately.
  const includeConversionUnits =
    !isEdit && supportsConversionUnits && conversionUnits.length > 0;

  // cost types without conversion units drop the saved ones. A schema that's
  // no longer a material doesn't send a budget, so its units are left alone.
  const getEditedConversionUnits = (): ConversionUnit[] | undefined => {
    if (!isEdit || !isMaterial) {
      return undefined;
    }

    return supportsConversionUnits ? conversionUnits : [];
  };

  const resetState = () => {
    setActiveStep(ModalStep.BASIC_DETAILS);
    setName(schema?.name ?? '');
    setLocationType(schema?.locationType ?? null);
    setColorId(schema?.colorId ?? 0);
    setMarkerId(schema?.markerId ?? 0);
    setIsMaterial(schema?.isMaterial ?? false);
    setStockBasedOption(
      schema?.budget?.stockBasedOption ?? StockBasedOption.NONE,
    );
    setStartEndReadingOption(
      schema?.budget?.startEndReadingOption ?? StartEndReadingOption.NONE,
    );
    setCostType(schema?.budget?.type ?? SchemaBudgetType.VARIABLE);
    setRate(schema?.budget?.rate ?? null);
    setUnit(schema?.budget?.unit ?? '');
    setConversionUnits(
      sortConversionUnits(schema?.budget?.conversionUnits ?? []),
    );
  };

  useEffect(() => {
    if (open) {
      resetState();
    }
  }, [open, schema?.id]);

  const handleConfirm = () => {
    onSave(
      {
        name,
        colorId,
        markerId,
        locationType,
        isMaterial,
        ...(isMaterial && {
          budget: {
            type: costType,
            stockBasedOption,
            startEndReadingOption,
            ...(costType !== SchemaBudgetType.VARIABLE && { unit }),
            ...(costType === SchemaBudgetType.FIXED &&
              rate !== null && { rate }),
            ...(includeConversionUnits && {
              conversionUnits: toConversionUnitInputs(conversionUnits),
            }),
          },
        }),
      },
      getEditedConversionUnits(),
    );

    onClose();
  };

  const handleStep = (value: number) => {
    const currentIndex = getStepIndex(activeStep, STEPS_ARRAY);
    const next = STEPS_ARRAY[currentIndex + value];

    if (next) {
      setActiveStep(next.value);
    }
  };

  const getContent = () => {
    switch (activeStep) {
      case ModalStep.BASIC_DETAILS:
        return <BasicDetailsTab />;
      case ModalStep.COST_TRACKING:
        return <CostTrackingTab />;
      case ModalStep.COST_AND_UNIT:
        return <CostAndUnitTab />;
      default:
        return null;
    }
  };

  const currentIndex = getStepIndex(activeStep, STEPS_ARRAY);
  const isLastStep = currentIndex === STEPS_ARRAY.length - 1;
  const showBack = activeStep !== ModalStep.BASIC_DETAILS;
  const showConfirmButton = isLastStep;

  const isDisabled = isCurrentStepDisabled({
    STEPS_ARRAY,
    activeStep,
    name,
    costType,
    rate,
    unit,
  });

  const rightArrowColor = isDisabled
    ? colors.neutral.gray
    : colors.neutral.white;

  const title = isEdit ? 'Edit Inventory Type' : 'Create Inventory Type';
  const confirmText = isEdit ? 'Save' : 'Create';

  const footer = (
    <div className={classes.footer}>
      {showBack ? (
        <Button
          onClick={() => handleStep(-1)}
          data-cy="createEditSchemaBackButton"
        >
          <Icon icon={ChevronLeftIcon} />
          Back
        </Button>
      ) : (
        <div />
      )}
      <div>
        <Button
          onClick={onClose}
          style={styles.cancelButton}
          data-cy="createEditSchemaCancelButton"
        >
          Cancel
        </Button>
        {showConfirmButton ? (
          <Button
            color="primary"
            variant="contained"
            onClick={handleConfirm}
            disabled={isDisabled}
            data-cy="createEditSchemaConfirmButton"
          >
            {confirmText}
          </Button>
        ) : (
          <Button
            color="primary"
            variant="contained"
            onClick={() => handleStep(1)}
            disabled={isDisabled}
            data-cy="createEditSchemaNextButton"
          >
            Next
            <Icon color={rightArrowColor} icon={ChevronRightIcon} />
          </Button>
        )}
      </div>
    </div>
  );

  const modalSize = activeStep === ModalStep.COST_AND_UNIT ? '55rem' : '41rem';

  return (
    <CreateEditSchemaModalContext.Provider
      value={{
        activeStep,
        setActiveStep,
        STEPS_ARRAY,
        name,
        setName,
        locationType,
        setLocationType,
        colorId,
        setColorId,
        markerId,
        setMarkerId,
        isMaterial,
        setIsMaterial,
        stockBasedOption,
        setStockBasedOption,
        startEndReadingOption,
        setStartEndReadingOption,
        costType,
        setCostType,
        rate,
        setRate,
        unit,
        setUnit,
        conversionUnits,
        setConversionUnits,
        isEdit,
      }}
    >
      <Modal
        title={title}
        footer={footer}
        open={open}
        onExited={resetState}
        contentStyle={styles.modalContent}
        width={modalSize}
        disableCloseOnOutsideClick
      >
        <div className={classes.modalContent}>
          <StepperSidebar />
          {getContent()}
        </div>
      </Modal>
    </CreateEditSchemaModalContext.Provider>
  );
};

export default CreateEditSchemaModal;
